import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import React from "react";
import NoData from "@/assets/noData.svg";
import { cn } from "@/lib/utils";
import { Button } from "../ui";
import RecursiveSubRows from "./RecursiveSubNRows";
import { DataTableCollapsibleNProps } from "./types";
import SortableTableHead from "../DataTable/SortableTableHead";

const DataTableCollapsibleRow = forwardRef(({
  data,
  columns,
  nestedColumns: rawNestedColumns,
  variant,
  onExpand,
  onExpandStateChange,
  renderExpandedContent,
  separateExpandColumn = false,
  globalFilter,
  setGlobalFilter,
  rowSelection,
  setRowSelection,
  setPageNumber,
  setPageSize,
  serverSidePagination,
  totalPages,
  pageNumber,
  pageSize,
  totalRecords,
  hideFooter = false,
  paginationArr,
  containerClassName,
  tableWrapperClassName,
  expandedCellClassName,
  allowMultipleExpansion = true,
  lastRow,
  icons,
  hideHeader,
  sorting: sortingProp,
  onSortingChange,
  enableSorting = true,
  manualSorting,
}: DataTableCollapsibleNProps, ref: any) => {
  const [expanded, setExpanded] = useState<any>({});
  const [nestedExpanded, setNestedExpanded] = useState<Record<string, boolean>>({});

  // Normalize nested columns to an array per depth level: [Level1Cols, Level2Cols, Level3Cols...]
  const normalizedNestedColumns = useMemo(() => {
    if (rawNestedColumns && rawNestedColumns.length > 0) {
      return rawNestedColumns;
    }

    return [];
  }, [rawNestedColumns]);

  const [pagination, setPagination] = useState({
    pageIndex: pageNumber || 0,
    pageSize: pageSize || 10,
  });
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
  const sorting = sortingProp ?? uncontrolledSorting;

  const _totalRecords = totalRecords ? totalRecords : data?.length || 0;

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      globalFilter,
      pagination,
      rowSelection,
      sorting,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      if (sortingProp === undefined) {
        setUncontrolledSorting(next);
      }
      onSortingChange?.(next);
    },
    enableSorting,
    enableSortingRemoval: true,
    manualSorting: manualSorting ?? !!serverSidePagination,
    onExpandedChange: (updater) => {
      setExpanded((prev: any) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        
        if (!allowMultipleExpansion) {
          const latestID = Object.keys(next).find((id) => !prev[id]);
          return latestID ? { [latestID]: true } : {};
        }
        
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    manualPagination: serverSidePagination,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    ...(serverSidePagination && {
      pageCount: totalPages,
    }),
  });

  // Infinite level expand/collapse all imperatively
  useImperativeHandle(ref, () => ({
    hasActiveExpansions: () => {
      const hasRootExpanded = Object.keys(expanded).length > 0;
      const hasNestedExpanded = Object.keys(nestedExpanded).length > 0;
      return hasRootExpanded || hasNestedExpanded;
    },
    expandAll: () => {
      table.toggleAllRowsExpanded(true);

      const newNestedState: Record<string, boolean> = {};
      const buildNestedKeys = (items: any[], parentId: string) => {
        items?.forEach((item, idx) => {
          const childRowId = `${parentId}_child_${idx}`;
          if (item?.children && item.children.length > 0) {
            newNestedState[childRowId] = true;
            buildNestedKeys(item.children, childRowId);
          }
        });
      };

      table.getRowModel().rows?.forEach((row) => {
        if ((row.original as any)?.children) {
          buildNestedKeys((row.original as any).children, row.id);
        }
      });

      setNestedExpanded(newNestedState);
    },
    collapseAll: () => {
      table.toggleAllRowsExpanded(false);
      setNestedExpanded({});
    }
  }));

  useEffect(() => {
    setPageNumber && setPageNumber(pagination.pageIndex);
    setPageSize && setPageSize(pagination.pageSize);
  }, [pagination]);

  const toggleChildExpanded = useCallback((id: string) => {
    setNestedExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isChildExpanded = useCallback((id: string) => !!nestedExpanded[id], [nestedExpanded]);

  const tableMeta = useMemo(() => ({
    toggleChildExpanded,
    isChildExpanded,
  }), [toggleChildExpanded, isChildExpanded]);

  const isSecondary = useMemo(() => variant === "secondary", [variant]);

  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 shadow-md overflow-hidden bg-white w-full",
        containerClassName
      )}
    >
      <div className={cn("lg:w-full", isSecondary ? "p-0 shadow-md" : "px-4", tableWrapperClassName)}>
        <Table>
          <TableHeader className={cn(isSecondary ? "bg-gray-100" : "")}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {separateExpandColumn ? (
                  <TableHead className="text-left h-[50px] text-primary w-[44px]" />
                ) : null}
                {headerGroup.headers.map((header) => (
                  <SortableTableHead
                    key={header.id}
                    header={header}
                    className={cn(
                      (header.column.columnDef.meta as any)?.align === "right"
                        ? "text-right"
                        : "text-left",
                      "h-[50px] text-primary font-bold text-sm"
                    )}
                  />
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => {
                const hasChildren = !!((row.original as any)?.children?.length);
                const visibleColCount = row.getVisibleCells().length + (separateExpandColumn ? 1 : 0);

                return (
                  <React.Fragment key={row.id}>
                    {/* Root Level Row */}
                    <TableRow
                      className={cn(
                        "border-gray-200",
                        isSecondary ? (row.getIsExpanded() ? "border-b-0" : "border-b") : "",
                        index % 2 === 1 ? "bg-gray-50/50" : ""
                      )}
                    >
                      {separateExpandColumn && hasChildren ? (
                        <TableCell className="h-[45px] w-[44px]">
                          <button
                            type="button"
                            onClick={() => {
                              row.getToggleExpandedHandler()();
                              onExpand?.(row);
                              onExpandStateChange?.(row, !row.getIsExpanded());
                            }}
                            className="flex items-center justify-center border border-primary text-primary rounded-full p-0.5 hover:bg-primary/10"
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                      ) : separateExpandColumn ? (
                        <TableCell className="h-[45px] w-[44px]" />
                      ) : null}

                      {row.getVisibleCells().map((cell: any, i) => {
                        const size = cell.column.columnDef.size;

                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              (cell.column.columnDef.meta as any)?.align === "right"
                                ? "text-right"
                                : "text-left",
                              "h-[45px]"
                            )}
                          >
                            {i === 0 && !separateExpandColumn && hasChildren ? (
                              <div className="flex items-center gap-2" style={{ width: size || "auto" }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    row.getToggleExpandedHandler()();
                                    onExpand?.(row);
                                    onExpandStateChange?.(row, !row.getIsExpanded());
                                  }}
                                  className="flex items-center justify-center border border-primary text-primary rounded-full p-0.5 hover:bg-primary/10"
                                >
                                  {row.getIsExpanded() ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                                {icons?.level1 || icons?.[0] || null}
                                <span className="flex text-wrap">
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </span>
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Infinite Recursive Children */}
                    {row.getIsExpanded() && hasChildren && (
                      <RecursiveSubRows
                        childrenData={(row.original as any).children}
                        parentId={row.id}
                        depth={0}
                        nestedColumns={normalizedNestedColumns}
                        expandedCellClassName={expandedCellClassName}
                        renderExpandedContent={renderExpandedContent}
                        icons={icons}
                        hideHeader={hideHeader}
                        isSecondary={isSecondary}
                        tableMeta={tableMeta}
                        visibleColCount={visibleColCount}
                      />
                    )}
                    {lastRow || null}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow className="w-full">
                <TableCell colSpan={columns.length + (separateExpandColumn ? 1 : 0)} className="py-10">
                  <div className="flex flex-col items-center justify-center">
                    <img src={NoData} className="w-[200px] h-auto" alt="No data" />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!hideFooter && _totalRecords > 0 && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-end p-4 gap-4 text-sm text-g-400 text-[15px]"
          )}
        >
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              className="rounded px-1 py-1"
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: Number(e.target.value),
                  pageIndex: 0, // Reset to first page when changing page size
                }))
              }
            >
              {[...(paginationArr ? paginationArr : [5, 10, 20, 50])].map(
                (size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                )
              )}
            </select>
          </div>
          <div>
            <span>
              {pagination.pageIndex * pagination.pageSize + 1} -{" "}
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                _totalRecords
              )}{" "}
              of {_totalRecords}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-2 py-1 shadow-none !bg-transparent"
            >
              <ChevronLeft className="w-6 h-6 text-g-400" />
            </Button>
            <Button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-2 py-1 shadow-none !bg-transparent"
            >
              <ChevronRight className="w-6 h-6 text-g-400" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default DataTableCollapsibleRow;
